/**
 * Where the mouse actually lives.
 *
 * ==========================================================================
 *  WHY LOOK IS NOT IN PlayerState ANY MORE
 * ==========================================================================
 *  Mouse deltas arrive per rendered frame; the simulation advances per fixed
 *  step. Previously the look was written straight into the player state as the
 *  events came in, which is smooth but unrecordable: there is no single number
 *  per step to write into a replay.
 *
 *  So the raw float angle accumulates here, and the *simulation* reads a
 *  quantised snapshot of it once per step (see StepInput). Two consequences,
 *  both wanted:
 *
 *    - the run is recordable, because a step's look is one integer pair;
 *    - the camera can still be driven from the raw float every frame, so a
 *      240 Hz display looks exactly as smooth as it did before while the
 *      simulation underneath stays at 120 Hz.
 * ==========================================================================
 */

import { LOOK } from '../config/gameplay';
import { clamp } from '../utils/math';
import { quantisePitch, quantiseYaw } from '../sim/StepInput';

export class LookAccumulator {
  private yawRadians = 0;
  private pitchRadians = 0;
  /** Sub-pixel remainder, carried rather than discarded. See `apply()`. */
  private carryX = 0;
  private carryY = 0;

  reset(yaw = 0, pitch = 0): void {
    this.yawRadians = yaw;
    this.pitchRadians = pitch;
    this.carryX = 0;
    this.carryY = 0;
  }

  /** Raw angles, for the camera only. Never for the simulation. */
  get yaw(): number {
    return this.yawRadians;
  }

  get pitch(): number {
    return this.pitchRadians;
  }

  /** What the simulation reads. Integers, and therefore recordable. */
  get yawUnits(): number {
    return quantiseYaw(this.yawRadians);
  }

  get pitchUnits(): number {
    return quantisePitch(this.pitchRadians);
  }

  /**
   * One mouse move, in raw pixels.
   *
   * The delta is consumed in *whole pixels*, with the remainder carried to the
   * next event. `movementX` is specified as a double, and some browser and
   * display combinations really do report fractions of a pixel - Safari on a
   * Retina panel among them.
   *
   * Rounding here makes "every look delta is a whole number of pixels times the
   * sensitivity" a property the game guarantees rather than something it hopes
   * the browser provides. That is what lets the submission analysis use the
   * pixel grid as a signal at all: without it, honest players on some machines
   * would look exactly like synthesised input. Carrying the remainder means the
   * rounding cannot accumulate into drift over a run.
   */
  apply(dx: number, dy: number, sensitivity: number): void {
    this.carryX += dx;
    this.carryY += dy;
    const wholeX = Math.trunc(this.carryX);
    const wholeY = Math.trunc(this.carryY);
    this.carryX -= wholeX;
    this.carryY -= wholeY;
    if (wholeX === 0 && wholeY === 0) return;

    const scale = LOOK.baseSensitivity * sensitivity;
    this.yawRadians -= wholeX * scale;
    this.pitchRadians = clamp(this.pitchRadians - wholeY * scale, LOOK.minPitch, LOOK.maxPitch);

    // Wrapped by modulo rather than by a single subtraction: one pointer-lock
    // event can carry more than a full turn, and a yaw that creeps outside
    // [-PI, PI] loses float precision the longer a session runs.
    const turn = Math.PI * 2;
    if (this.yawRadians > Math.PI || this.yawRadians < -Math.PI) {
      this.yawRadians -= turn * Math.round(this.yawRadians / turn);
    }
  }
}
